import createFetchClient from 'openapi-fetch'
import createClient from 'openapi-react-query'
import { useAuthStore } from '@/stores/authStore'
import type { paths } from './api-types'

/**
 * Utility function to extract cookies from document.cookie
 */
function getCookie(name: string): string | null {
	if (typeof document === 'undefined') return null

	const value = `; ${document.cookie}`
	const parts = value.split(`; ${name}=`)
	if (parts.length === 2) {
		return parts.pop()?.split(';').shift() || null
	}
	return null
}

// Create the API client with the base URL
export const apiClient = createFetchClient<paths>({
	baseUrl: 'https://api.gaiadex.certichain.io',
	//baseUrl: 'http://localhost:8888',
	//baseUrl: 'http://localhost:8000',
	credentials: 'include',
	headers: {
		Accept: 'application/json',
	},
})
export const apiQuery = createClient(apiClient)

// Never use apiClient with Fetch in React.
apiClient.use({
	async onRequest({ request }) {
		if (request.body instanceof FormData) {
			request.headers.delete('Content-Type')
		} else if (request.body && !request.headers.has('Content-Type')) {
			if (
				typeof request.body === 'string' ||
				(request.body instanceof Blob && !(request.body instanceof FormData))
			) {
				// Don't set Content-Type
			} else if (request.body instanceof ArrayBuffer || request.body instanceof ReadableStream) {
				// Don't set Content-Type
			} else {
				// Set Content-Type
				request.headers.set('Content-Type', 'application/json')
			}
		}

		// Get CSRF token from cookies first, then fallback to localStorage/sessionStorage
		const csrfToken =
			getCookie('csrftoken') ||
			localStorage.getItem('csrf_token') ||
			sessionStorage.getItem('csrf_token')

		if (csrfToken) {
			request.headers.set('X-CSRFToken', csrfToken)
		}

		// Add the Authorization header if an access token exists
		const { accessToken } = useAuthStore.getState().auth
		if (accessToken) {
			request.headers.set('Authorization', `Bearer ${accessToken}`)
		}

		if (request.body instanceof FormData) {
			request.headers.delete('Content-Type')
		}

		return request
	},
	async onResponse({ request, response }) {
		// If the response is not 401, just return it
		if (response.status !== 401) {
			return response
		}

		// Prevent infinite refresh loops
		if (response.url.includes('/api/v1/siwe/refresh/')) {
			console.error('Refresh token failed, logging out.')
			useAuthStore.getState().auth.clearAuth()
			return response
		}

		const { refreshToken, setTokens, clearAuth } = useAuthStore.getState().auth

		if (!refreshToken) {
			clearAuth()
			return response
		}

		try {
			// Attempt to refresh the token
			const refreshResponse = await apiClient.POST('/api/v1/siwe/refresh/', {
				body: {
					refresh: refreshToken,
					access: '', // This field is not required, as it is supposed to be response-only, but included for sake of api-generator
					valid: true, // This field is not required, as it is supposed to be response-only, but included for sake of api-generator
				},
			})

			if (refreshResponse.data) {
				const { access, refresh } = refreshResponse.data
				setTokens({ accessToken: access, refreshToken: refresh })

				// Retry the original request with the new token
				const retryRequest = new Request(request.url, {
					method: request.method,
					headers: request.headers,
					body: request.body,
					credentials: request.credentials,
				})
				retryRequest.headers.set('Authorization', `Bearer ${access}`)

				return fetch(retryRequest)
			} else {
				// If refresh fails, clear auth
				clearAuth()
				return response
			}
		} catch (error) {
			console.error('Error refreshing token:', error)
			clearAuth()
			return response
		}
	},
})

/**
 * Helper function to get headers for multipart requests
 * Reuses the same logic as apiClient middleware
 */
export function getMultipartHeaders(): HeadersInit {
	const headers: HeadersInit = {
		Accept: 'application/json',
	}

	// Get CSRF token from cookies first, then fallback to localStorage/sessionStorage
	const csrfToken =
		getCookie('csrftoken') ||
		localStorage.getItem('csrf_token') ||
		sessionStorage.getItem('csrf_token')

	if (csrfToken) {
		headers['X-CSRFToken'] = csrfToken
	}

	// Add the Authorization header if an access token exists
	const { accessToken } = useAuthStore.getState().auth
	if (accessToken) {
		headers['Authorization'] = `Bearer ${accessToken}`
	}

	return headers
}

/**
 * Get the base URL used by apiClient
 */
export function getApiBaseUrl(): string {
	return 'https://api.gaiadex.certichain.io'
	// return 'http://localhost:8888'
	// return 'http://localhost:8000'
}

// Export types for convenience
export type { paths } from './api-types'
